import React, { useState, useEffect } from 'react';
import { RefreshCw, Briefcase, Search } from 'lucide-react';
import { apiGet } from '@/services/api';
import { Button, Card, LoadingState, EmptyState } from '@/components/common';
import type { HalCollection } from '@/types';

interface ProjectStatusData {
  id: number;
  name: string;
  code: string;
  color: string;
}

export function StatusProjectsPage() {
  const [statuses, setStatuses] = useState<ProjectStatusData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const loadStatuses = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiGet<HalCollection<ProjectStatusData>>('/auth/data/admin/project-statuses');
      setStatuses(response._embedded?.elements || []);
    } catch (err) {
      console.error('Failed to load project statuses:', err);
      setError('Eroare la încărcarea statusurilor de proiecte');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadStatuses();
  }, []);

  const filteredStatuses = statuses.filter(status => {
    const query = searchQuery.toLowerCase();
    return status.name.toLowerCase().includes(query) || status.code?.toLowerCase().includes(query);
  });

  if (isLoading) {
    return <LoadingState message="Se încarcă statusurile..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-orange-500 text-white">
            <Briefcase className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Status Proiecte</h1>
            <p className="text-gray-600 mt-1">Nomenclator statusuri pentru proiecte</p>
          </div>
        </div>
        <div className="flex gap-3">
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full font-medium self-center">
            {filteredStatuses.length} statusuri
          </span>
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="w-4 h-4" />}
            onClick={loadStatuses}
          >
            Reîncarcă
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      <Card className="overflow-hidden">
        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Caută statusuri..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {filteredStatuses.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon="list"
              title="Nu există statusuri"
              description={searchQuery ? "Nu s-au găsit statusuri care să corespundă căutării." : "Nu există statusuri de proiecte în sistem."}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Nume status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Cod
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Culoare
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredStatuses.map((status) => (
                  <tr key={status.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: status.color || '#6B7280' }}
                        />
                        <span className="font-medium text-gray-900">{status.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-mono">
                      {status.code || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded border border-gray-200"
                          style={{ backgroundColor: status.color || '#6B7280' }}
                        />
                        <span className="text-sm text-gray-600 font-mono">
                          {status.color || '-'}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
